import { Redirect, Route } from 'react-router-dom'
import { CIMSModule, pathify, resolveValueOrArray } from '@cims-online/shared'
import { useAuthContext } from 'context/AuthContext'
import { notificationHandler } from 'hooks/notificationHandler'
import { ProtectedRouteProps } from './types'
import { useRouteMatches } from '../../hooks/useRouteMatches'
import { NODE_ENV } from 'config'

/**
 * designed with inspiration from [here](https://v5.reactrouter.com/web/example/auth-workflow)
 *
 * @see Login component for more information
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  permissions,
  developer,
  children,
  ...rest
}) => {
  const { param } = useRouteMatches()
  const { user } = useAuthContext()
  const projectId = param('projectId')
  return (
    <Route
      {...rest}
      render={({ location: { pathname: from } }) => {
        if (!user) return <Redirect to={{ pathname: '/', state: { from } }} />

        const to = resolveValueOrArray(permissions ?? [])

        /**
         * if you don't set developer or permissions it's open
         */
        let authorized = true
        if (permissions)
          authorized = user.someAuthorized(to, projectId).authorized
        else if (developer) authorized = user.developer === true

        if (authorized) return children

        notificationHandler({
          title:
            'You do not have permission to access this resource. Please reach out to the primary CIMS contact if you feel this is incorrect.',
          alternateType: 'warning',
          details:
            NODE_ENV === 'production' ? undefined : (
              <pre style={{ maxHeight: 500, overflow: 'auto' }}>
                {JSON.stringify(
                  {
                    from,
                    to: rest.path,
                    routePermissions: developer
                      ? 'developer'
                      : to.map(({ name, id }) => ({ id, name })),
                    user: {
                      ...user.properties,
                      projectId,
                    },
                  },
                  null,
                  2
                )}
              </pre>
            ),
        })

        const { pathname } = CIMSModule.Home
        if (projectId) return <Redirect to={pathify(projectId, pathname)} />
        return <Redirect to={pathify(pathname)} />
      }}
    />
  )
}
